import { Button, Form, Input, Select, message } from 'antd'
import { useRef, useState } from 'react'
import { CheckCircleFilled, LockOutlined, SendOutlined } from '@ant-design/icons'
import { trackConversion } from '../lib/analytics'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

type Props = {
  copy: any
}

export default function ContactForm({ copy }: Props) {
  const [form] = Form.useForm()
  const [sent, setSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const hasStarted = useRef(false)
  const { executeRecaptcha } = useGoogleReCaptcha()

  const f = copy.form || {}

  const markStarted = () => {
    if (!hasStarted.current) {
      hasStarted.current = true
      trackConversion('form_start')
    }
  }

  const onFinish = async (values: any) => {
    trackConversion('form_submit', { challenge: values.challenge || 'not_specified' })
    setLoading(true)

    try {
      if (!executeRecaptcha) {
        throw new Error('reCAPTCHA has not been initialized yet')
      }

      // Execute reCAPTCHA to get the token
      const recaptchaToken = await executeRecaptcha('contact_form_submit')

      const FUNCTION_URL = 'https://sendcontactemail-vwv7zi4ava-uc.a.run.app'
      
      const payload = {
        name: typeof values.name === 'string' ? values.name.trim() : values.name,
        company: typeof values.company === 'string' ? values.company.trim() : values.company,
        role: typeof values.role === 'string' ? values.role.trim() : values.role,
        email: typeof values.email === 'string' ? values.email.trim() : values.email,
        phone: typeof values.phone === 'string' ? values.phone.trim() : values.phone,
        companySize: typeof values.companySize === 'string' ? values.companySize.trim() : values.companySize,
        challenge: typeof values.challenge === 'string' ? values.challenge.trim() : values.challenge,
        message: typeof values.message === 'string' ? values.message.trim() : values.message,
        recaptchaToken
      }

      const response = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        throw new Error('Failed to send message')
      }

      setSent(true)
    } catch (error) {
      console.error(error)
      message.error(f.error || 'There was an error sending your message. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  if (sent) {
    return (
      <div className="contactSuccessCard">
        <div className="contactSuccess__icon">
          <CheckCircleFilled />
        </div>
        <h3>{f.success || 'Inquiry Received'}</h3>
        <p>{f.successSubtitle || 'Our senior advisory partners will review your briefing and contact you confidentially.'}</p>
        <Button
          type="default"
          className="contactSuccess__reset"
          onClick={() => {
            setSent(false)
            form.resetFields()
            hasStarted.current = false
          }}
        >
          {f.reset || 'Send Another Message'}
        </Button>
      </div>
    )
  }

  return (
    <div className="contactFormContainer">
      <div className="contactFormHeader">
        <p className="contactFormSubtitle">
          {f.subtitle || "Tell us what is making execution harder right now."}
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        className="contactForm"
        onFinish={onFinish}
        onFocus={markStarted}
        requiredMark={false}
      >
        <div className="formGrid">
          <Form.Item
            label={f.name}
            name="name"
            rules={[
              { required: true, message: f.nameRequired || 'Please enter your name' },
              { max: 100, message: 'Name cannot exceed 100 characters' }
            ]}
          >
            <Input size="large" maxLength={100} placeholder={f.name} />
          </Form.Item>
          <Form.Item
            label={f.company}
            name="company"
            rules={[
              { required: true, message: f.companyRequired || 'Please enter your organization' },
              { max: 100, message: 'Company cannot exceed 100 characters' }
            ]}
          >
            <Input size="large" maxLength={100} placeholder={f.company} />
          </Form.Item>
        </div>

        <div className="formGrid">
          <Form.Item
            label={f.role}
            name="role"
            rules={[{ required: true, message: f.roleRequired || 'Please select your role' }]}
          >
            <Select size="large" placeholder={f.role} options={f.roleOptions} />
          </Form.Item>
          <Form.Item
            label={f.email}
            name="email"
            rules={[
              { required: true, message: f.emailRequired || 'Please enter your work email' },
              { type: 'email', message: f.emailValid || 'Enter a valid corporate email' },
              { max: 150, message: 'Email cannot exceed 150 characters' }
            ]}
          >
            <Input size="large" maxLength={150} placeholder="executive@company.com" />
          </Form.Item>
        </div>

        <div className="formGrid">
          <Form.Item
            label={f.phone || 'Phone Number'}
            name="phone"
            rules={[
              { required: true, message: f.phoneRequired || 'Please enter your phone number' },
              { max: 20, message: 'Phone cannot exceed 20 characters' },
              { pattern: /^\+?[0-9\s\-()]+$/, message: 'Please enter a valid phone number' }
            ]}
          >
            <Input type="tel" size="large" maxLength={20} placeholder="+1 (809) 000-0000" />
          </Form.Item>
          <Form.Item
            label={f.companySize || 'Company Size'}
            name="companySize"
            rules={[{ required: true, message: f.companySizeRequired || 'Please select the company size' }]}
          >
            <Select size="large" placeholder={f.companySize || 'Company Size'} options={f.companySizeOptions} />
          </Form.Item>
        </div>

        <Form.Item
          label={f.challenge}
          name="challenge"
          rules={[{ required: true, message: f.challengeRequired || 'Please select the challenge you are facing' }]}
        >
          <Select size="large" placeholder={f.challenge} options={f.challengeOptions} />
        </Form.Item>

        <Form.Item
          label={f.message}
          name="message"
          rules={[{ max: 1000, message: 'Message cannot exceed 1000 characters' }]}
        >
          <Input.TextArea showCount rows={3} maxLength={1000} placeholder={f.message} className="contactForm__textarea" />
        </Form.Item>

        <div className="contactFormFooter">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            className="goldBtn contactForm__submit"
            icon={<SendOutlined />}
            loading={loading}
          >
            {f.send}
          </Button>
          <span className="contactFormPrivacy">
            <LockOutlined /> {f.privacy || 'Strict Executive Confidentiality'}
          </span>
        </div>
      </Form>
    </div>
  )
}
